<template>
  <div class="rdoc" :data-testid="`reponse-documents-${question.id}${reponse ? '-' + reponse : ''}`">
    <p class="rdoc-consigne">
      <i class="fas fa-paperclip" aria-hidden="true"></i>
      <span>
        <strong v-if="reponse">« {{ reponse }} » — </strong>{{ consigne }}
      </span>
    </p>

    <ul v-if="documents.length" class="rdoc-liste">
      <li v-for="document in documents" :key="document.id" class="rdoc-fichier">
        <!-- Le nom passe à la ligne plutôt que d'être tronqué : c'est par lui
             qu'on reconnaît le fichier. -->
        <Button
          variant="ghost"
          size="sm"
          icon="fas fa-download"
          class="rdoc-nom"
          :aria-label="`Télécharger ${document.nomFichier}`"
          @click="contexte.telecharger(document)"
        >
          {{ document.nomFichier }}
        </Button>
        <span class="rdoc-meta">{{ formatTailleFichier(document.tailleOctets) }}</span>
        <Button
          v-if="!disabled && contexte.peutRetirer(document)"
          variant="ghost-danger"
          size="sm"
          icon="fas fa-trash-alt"
          class="rdoc-retirer"
          :aria-label="`Retirer ${document.nomFichier}`"
          @click="aRetirer = document"
        >
          Retirer
        </Button>
      </li>
    </ul>
    <p v-else-if="disabled" class="rdoc-vide">Aucun document joint.</p>

    <FileUploadZone
      v-if="!disabled"
      compact
      multiple
      action-labels
      accept="*/*"
      :max-size="TAILLE_MAX_DOCUMENT"
      :max-size-label="`Un fichier dépasse ${TAILLE_MAX_DOCUMENT_MO} Mo`"
      icon="fas fa-paperclip"
      drop-text="Glissez vos fichiers ici"
      drop-active-text="Déposez vos fichiers ici"
      button-label="Ajouter des documents"
      uploading-text="Envoi en cours…"
      :hint="`Tous types de fichiers, ${TAILLE_MAX_DOCUMENT_MO} Mo maximum chacun`"
      :uploading="envoiEnCours"
      :input-test-id="`reponse-documents-input-${question.id}`"
      @files-selected="(fichiers) => contexte.deposer(question.id, reponse, fichiers)"
    />

    <!-- Un fichier retiré l'est pour de bon : on le confirme, nom à l'appui. -->
    <DialogConfirm
      :model-value="Boolean(aRetirer)"
      title="Retirer ce document ?"
      :item-name="aRetirer?.nomFichier"
      message="Le document ne sera plus joint à cette réponse."
      confirm-label="Retirer le document"
      @update:model-value="(ouvert) => { if (!ouvert) aRetirer = null }"
      @close="aRetirer = null"
      @confirm="confirmerRetrait"
    />
  </div>
</template>

<script setup>
/**
 * La zone de dépôt ouverte par une réponse (ou par une zone de texte) : la
 * consigne écrite par l'administrateur, les documents déjà joints, et de quoi
 * en ajouter. Les fichiers partent tout de suite, sans attendre l'enregistrement
 * du questionnaire. Le contexte (qui dépose, sur quelle demande) vient de
 * l'écran parent (`fournirDocumentsReponses`), transmis par le rendu de la question.
 */
import { computed, ref } from 'vue'
import { Button, DialogConfirm, FileUploadZone } from '../ui'
import { formatTailleFichier } from '../../utils/formatting.js'
import { TAILLE_MAX_DOCUMENT } from '../../../shared/documents-reponses.js'

const props = defineProps({
  question: { type: Object, required: true },
  /** Le libellé de la réponse qui ouvre le dépôt, ou `null` (zone de texte). */
  reponse: { type: String, default: null },
  consigne: { type: String, required: true },
  /** Le contexte fourni par l'écran (`fournirDocumentsReponses`). */
  contexte: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
})

const TAILLE_MAX_DOCUMENT_MO = Math.round(TAILLE_MAX_DOCUMENT / (1024 * 1024))

const documents = computed(() => props.contexte.documentsDe(props.question.id, props.reponse))
const envoiEnCours = computed(() => props.contexte.envoiEnCours.value === props.contexte.cleDepot(props.question.id, props.reponse))

/** Le document dont on demande le retrait, ou `null`. */
const aRetirer = ref(null)

async function confirmerRetrait() {
  const document = aRetirer.value
  aRetirer.value = null
  if (document) await props.contexte.retirer(document)
}
</script>

<style scoped>
.rdoc {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
  padding: var(--spacing-sm);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-secondary);
}

.rdoc-consigne {
  display: flex;
  gap: var(--spacing-xs);
  align-items: flex-start;
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.rdoc-consigne i {
  margin-top: 3px;
  color: var(--color-primary);
}

.rdoc-liste {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.rdoc-fichier {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs) var(--spacing-sm);
  min-width: 0;
}

/* Le nom s'enroule au lieu d'être coupé. */
.rdoc-nom {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: left;
}

.rdoc-meta {
  flex-shrink: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.rdoc-retirer {
  margin-left: auto;
}

.rdoc-vide {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}
</style>
